import React, { useEffect, useState } from 'react';

import type { DescriptionsProps } from 'antd';
import { ConfigProvider, Descriptions, message, Skeleton } from 'antd';
import enUS from 'antd/lib/locale/en_US';
import { useTranslation } from 'react-i18next';
import type {
  Organization,
  ReducedOrganization,
  User,
} from '../../Types/interfaces';
import { GetOrganizationById, GetUserById } from '../../helper/apiHelper';

interface OrganizationComponentProps {
  organization: ReducedOrganization;
}

const OrganizationComponent = ({
  organization,
}: OrganizationComponentProps): React.ReactNode => {
  const { t } = useTranslation();
  const [extendedOrganization, setExtendedOrganization] =
    useState<Organization>();
  const [representative, setRepresentative] = useState<User>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const org = await GetOrganizationById(organization.id.toString());
        setExtendedOrganization(org);
        const representativeResp = await GetUserById(
          org.representativeId.toString()
        );
        setRepresentative(representativeResp);
      } catch (error) {
        console.error(error);
        message.error(t('legDepOrgComp.loadError'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [organization.id, t]);

  const SkeletonDescriptions = (
    <div>
      <Skeleton active paragraph={{ rows: 10 }}></Skeleton>
    </div>
  );

  if (isLoading) {
    return SkeletonDescriptions;
  }

  if (extendedOrganization && representative) {
    const items: DescriptionsProps['items'] = [
      {
        label: t('legDepOrgComp.orgRep'),
        children: extendedOrganization.representativeName,
        span: 3,
      },
      {
        label: t('legDepOrgComp.desc'),
        children: extendedOrganization.description,
        span: 3,
      },
      {
        label: t('legDepOrgComp.city'),
        children: extendedOrganization.city,
      },
      {
        label: t('legDepOrgComp.street'),
        children: extendedOrganization.street,
      },
      {
        label: t('legDepOrgComp.streetNr'),
        children: extendedOrganization.streetNumber,
      },
      {
        label: t('legDepOrgComp.phone'),
        children: extendedOrganization.phoneNumber,
      },
      {
        label: t('legDepOrgComp.fax'),
        children: extendedOrganization.fax,
      },
      {
        label: t('legDepOrgComp.email'),
        children: extendedOrganization.email,
      },
      {
        label: t('legDepOrgComp.fisc'),
        children: extendedOrganization.fiscalCode,
      },
      {
        label: t('legDepOrgComp.account'),
        children: extendedOrganization.fiscalAccount,
      },
      {
        label: t('legDepOrgComp.iban'),
        children: extendedOrganization.iban,
      },
      {
        label: t('legDepOrgComp.bachelorStudents'),
        children: extendedOrganization.bachelorStudents,
      },
      {
        label: t('legDepOrgComp.masterStudents'),
        children: extendedOrganization.masterStudents,
      },
    ];

    return (
      <>
        <ConfigProvider locale={enUS}>
          <Descriptions
            bordered
            title={extendedOrganization.name}
            column={{ xs: 1, sm: 1, md: 1, lg: 2, xl: 3, xxl: 3 }}
            items={items}
          />
        </ConfigProvider>
        <br></br>
      </>
    );
  }
};
export default OrganizationComponent;
